import { ReactElement } from 'react'

import { FAL_ANGLE_RIGHT } from 'const/icons'
import { Stack } from 'syf-component-library/ui/spacing'

import { customCardArtModalContainerId } from 'const/modalContainerId'
import { useCreditAccounts } from 'const/syfApiQueries'
import { CARECREDIT } from 'const/account/clients'
import { useSetAtom } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import {
  ArrowIcon,
  CampaignButton,
  CampaignLink,
  CampaignTitle,
  Image,
  LowerRowText,
  Subtitle,
  UpperRowText,
  YieldCard
} from '../subcomponents'

export interface DynamicYieldCardProps {
  campaignData: {
    image: string
    title: string
    subtitle: string
    link: string
    cta: string
    id?: string
  }
}

const DynamicYieldCard = ({
  campaignData
}: DynamicYieldCardProps): ReactElement => {
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)

  const { image, title, subtitle, link, cta, id } = campaignData

  const isCustomCardArt = id === 'customCardArt'
  const displayCampaignLink = Boolean(cta && link)
  const displayImage = Boolean(image)

  const { data: { accounts = [] } = {} } = useCreditAccounts()

  // Selected accountId should be the carecredit account for CustomCardArt and should not be more or less than one
  const accountId = accounts.find(
    (acc) => acc?.product?.portfolio.toLowerCase() === CARECREDIT
  )?.info?.['cipher.accountId']

  const customcardArtButtonId = customCardArtModalContainerId(accountId)

  const clickHandler = () => {
    updateModalStatus('CustomCardArt', customcardArtButtonId)
    setSelectedAccount(accountId)
  }
  const dataCyPrefix = isCustomCardArt ? 'customcardart' : 'general'

  return (
    <YieldCard key={title}>
      <UpperRowText>
        {displayImage && <Image src={image} alt="" />}
        <div>
          <CampaignTitle as="h3" data-cy={`${dataCyPrefix}-campaign-title`}>
            {title}
          </CampaignTitle>
          <Stack size="xsmall" />
          <Subtitle data-cy={`${dataCyPrefix}-campaign-sub-title`}>
            {subtitle}
          </Subtitle>
        </div>
      </UpperRowText>
      <Stack />
      <LowerRowText>
        {isCustomCardArt ? (
          <CampaignButton
            buttonType="text"
            onClick={clickHandler}
            data-object="button"
            data-reason="customize now"
            data-type="custom card"
            data-analytics="clicktrackingevent"
            data-cy="customcardart-campaign-button"
          >
            {cta}
            <ArrowIcon icon={FAL_ANGLE_RIGHT} />
          </CampaignButton>
        ) : (
          displayCampaignLink && (
            <CampaignLink data-cy="campaign-button" href={link} target="_blank">
              {cta}
              <ArrowIcon icon={FAL_ANGLE_RIGHT} />
            </CampaignLink>
          )
        )}
      </LowerRowText>
    </YieldCard>
  )
}

export default DynamicYieldCard
